import { t } from "ttag";

import { EmbeddingSettingsCard } from "metabase/admin/settings/components/EmbeddingSettings";
import { NewEmbedButton } from "metabase/admin/settings/components/EmbeddingSettings/NewEmbedButton/NewEmbedButton";
import { UpsellBanner } from "metabase/common/components/upsells/components";
import { PLUGIN_CONTENT_TRANSLATION } from "metabase/content-translation/plugins";
import {
  PLUGIN_ADMIN_SETTINGS,
  PLUGIN_EMBEDDING_IFRAME_SDK_SETUP,
} from "metabase/plugins";
import { useSelector } from "metabase/redux";
import { getUpgradeUrl } from "metabase/selectors/settings";
import { useSetting } from "metabase/settings";
import { SettingTitle, SettingsSection } from "metabase/settings-components";
import { Box, Text } from "metabase/ui";

import { EmbeddedResources } from "../../widgets/PublicLinksListing/EmbeddedResources";
import { EmbeddingSecretKeyWidget } from "../EmbeddingSecretKeyWidget";

type Props = {
  showEmbeddingSdkSettings?: boolean;
  showContentTranslationSettings?: boolean;
};

export function SharedCombinedEmbeddingSettings({
  showContentTranslationSettings,
}: Props) {
  const isSimpleEmbedFeatureAvailable =
    PLUGIN_EMBEDDING_IFRAME_SDK_SETUP.isEnabled();
  const isGuestEmbedsEnabled = useSetting("enable-embedding-static");

  const upgradeUrl = useSelector((state) =>
    getUpgradeUrl(state, { utm_content: "embedding-settings" }),
  );

  const { triggerUpsellFlow } = PLUGIN_ADMIN_SETTINGS.useUpsellFlow({
    campaign: "enterprise",
    location: "embedding-settings",
  });

  return (
    <>
      <EmbeddingSettingsCard
        title={t`Enable guest embeds`}
        description={t`A secure way to embed charts and dashboards, without single sign-on, when you don’t want to offer ad-hoc querying or chart drill-through.`}
        settingKey="enable-embedding-static"
        actionButton={<NewEmbedButton forceIsGuest />}
        sdk-setting-card
        testId="guest-embeds-setting-card"
      />

      {!isSimpleEmbedFeatureAvailable && (
        <UpsellBanner
          title={t`Upgrade to Metabase Pro for more powerful embedding methods`}
          campaign="embedding-methods"
          location="embedding-page"
          buttonText={t`Upgrade`}
          buttonLink={upgradeUrl}
          onClick={triggerUpsellFlow}
          dismissible
        >
          <Text c="text-secondary" lh="md">
            {t`Embed charts, dashboards with drill-throughs, or even the query builder into your own application using customizable components. Secure your embeds with single sign-on.`}
          </Text>
        </UpsellBanner>
      )}

      <SettingsSection>
        <EmbeddingSecretKeyWidget />
      </SettingsSection>

      {isGuestEmbedsEnabled && (
        <SettingsSection>
          <Box data-testid="embedded-resources">
            <SettingTitle
              id="static-embeds"
              fz="lg"
              mb="lg"
            >{t`Published embeds`}</SettingTitle>

            <EmbeddedResources />
          </Box>
        </SettingsSection>
      )}

      {showContentTranslationSettings && (
        <PLUGIN_CONTENT_TRANSLATION.ContentTranslationConfiguration />
      )}
    </>
  );
}
